"use client"

import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  TextField,
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  Breadcrumbs,
  Link as MuiLink,
  Divider,
} from "@mui/material"
import { Link } from "react-router-dom"
import { getUserCredits, manageUserCredits, getCreditTransactions, resetCreditState } from "../slices/creditSlice"
import DashboardLayout from "../components/DashboardLayout"

const CreditManagementPage = () => {
  const { userId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { userInfo } = useSelector((state) => state.auth)
  const { userCredits, transactions, loading, error, success } = useSelector((state) => state.credits)
  const [activeTab, setActiveTab] = useState("domestic")
  const [formData, setFormData] = useState({
    credits: "",
    pricePerSMS: "",
    remarks: "",
    transactionType: "credit",
  })
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" })

  useEffect(() => {
    if (!userId) {
      navigate("/users")
      return
    }

    dispatch(getUserCredits(userId))
  }, [userId, dispatch, navigate])

  useEffect(() => {
    if (!userId) return
    // relies on initial value activeTab === "domestic"
    dispatch(getCreditTransactions({ userId, creditType: activeTab ? activeTab : "domestic" }))
    // Intentionally not depending on activeTab to avoid double-fetch with handleTabChange
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, dispatch])

  useEffect(() => {
    if (success) {
      setAlert({
        open: true,
        message: "Credits updated successfully",
        severity: "success",
      })
      setFormData({
        credits: "",
        pricePerSMS: "",
        remarks: "",
        transactionType: "credit",
      })
      dispatch(resetCreditState())
    }
  }, [success, dispatch])

  useEffect(() => {
    if (error) {
      setAlert({
        open: true,
        message: error,
        severity: "error",
      })
      dispatch(resetCreditState())
    }
  }, [error, dispatch])

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
    dispatch(getCreditTransactions({ userId, creditType: newValue }))
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!formData.credits || isNaN(formData.credits) || Number(formData.credits) <= 0) {
      setAlert({
        open: true,
        message: "Please enter a valid credit amount",
        severity: "error",
      })
      return
    }

    dispatch(
      manageUserCredits({
        userId,
        creditData: {
          creditType: activeTab,
          amount: Number(formData.credits),
          pricePerSMS: formData.pricePerSMS || 0,
          remarks: formData.remarks || "",
          transactionType: formData.transactionType,
        },
      }),
    )
  }

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false })
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`
  }

  if (!userCredits && loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <MuiLink component={Link} to="/dashboard" color="inherit" underline="hover">
            Dashboard
          </MuiLink>
          <MuiLink component={Link} to="/users" color="inherit" underline="hover">
            Users
          </MuiLink>
          <Typography color="text.primary">Manage Credits</Typography>
        </Breadcrumbs>

        <Snackbar
          open={alert.open}
          autoHideDuration={6000}
          onClose={handleCloseAlert}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
            {alert.message}
          </Alert>
        </Snackbar>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" component="h1">
            Manage Credits
          </Typography>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                minWidth: 120,
              },
              "& .Mui-selected": {
                color: "#008069",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#008069",
              },
            }}
          >
            <Tab label="Domestic Credits" value="domestic" />
            <Tab label="International Credits" value="international" />
            <Tab label="Button Credits" value="button" />
          </Tabs>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Assignor Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1">{userInfo?.user?.name || "Admin"}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">{userInfo?.user?.email}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Typography variant="body1">{userInfo?.user?.status || "Active"}</Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Assignee Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1">{userCredits?.name}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">{userCredits?.email}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Typography variant="body1">{userCredits?.status}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Created By
                </Typography>
                <Typography variant="body1">{userCredits?.createdBy}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Created By Name
                </Typography>
                <Typography variant="body1">{userCredits?.createdByName}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Type
                </Typography>
                <Typography variant="body1">{userCredits?.role}</Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {activeTab === "domestic" ? "Domestic" : activeTab === "international" ? "International" : "Button"}{" "}
                  Credits
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {activeTab === "domestic"
                    ? userCredits?.domesticCredits
                    : activeTab === "international"
                      ? userCredits?.internationalCredits
                      : userCredits?.buttonCredits}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Assign Credits
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <form onSubmit={handleSubmit}>
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Credits"
                    name="credits"
                    value={formData.credits}
                    onChange={handleInputChange}
                    placeholder="Type here"
                    required
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Price Per SMS"
                    name="pricePerSMS"
                    value={formData.pricePerSMS}
                    onChange={handleInputChange}
                    placeholder="0"
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Remarks"
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleInputChange}
                    placeholder="Type here"
                    multiline
                    rows={2}
                    sx={{ mb: 2 }}
                  />
                </Box>

                <RadioGroup
                  row
                  name="transactionType"
                  value={formData.transactionType}
                  onChange={handleInputChange}
                  sx={{ mb: 2 }}
                >
                  <FormControlLabel value="credit" control={<Radio />} label="Credit" />
                  <FormControlLabel value="debit" control={<Radio />} label="Debit" />
                </RadioGroup>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  sx={{
                    bgcolor: "#008069",
                    "&:hover": { bgcolor: "#128C7E" },
                    textTransform: "none",
                  }}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Save"}
                </Button>
              </form>
            </Paper>
          </Grid>
        </Grid>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Transaction History
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : transactions.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>SR. NO.</TableCell>
                    <TableCell>NAME</TableCell>
                    <TableCell>CREDITS</TableCell>
                    <TableCell>BY/TO</TableCell>
                    <TableCell>DATE</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction, index) => (
                    <TableRow key={transaction._id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{userCredits?.name}</TableCell>
                      <TableCell sx={{ color: transaction.transactionType === "credit" ? "green" : "red" }}>
                        {transaction.transactionType === "credit" ? "+" : "-"}
                        {transaction.amount}
                        <Box sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                          Before Credits: {transaction.beforeCredits}
                          <br />
                          After Credits: {transaction.afterCredits}
                          {transaction.remarks && transaction.remarks.includes("Refund for") && (
                            <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: "italic" }}>
                              ({transaction.remarks})
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {transaction.transactionType === "credit" ? "By:" : "To:"}
                        {transaction.assignedBy?.name || "Admin"}
                      </TableCell>
                      <TableCell>
                        {formatDate(transaction.createdAt)}
                        <br />
                        {transaction.remarks && (() => {
                          let desc = transaction.remarks;
                          const targetTrue = " (Report manipulated: true)";
                          const targetFalse = " (Report manipulated: false)";
                          if (desc.endsWith(targetTrue)) {
                            desc = desc.slice(0, -targetTrue.length).trim();
                          } else if (desc.endsWith(targetFalse)) {
                            desc = desc.slice(0, -targetFalse.length).trim();
                          }

                          return (
                            <span >
                             {desc}
                            </span>
                          );
                        })()}
                       <br/>
                        {(transaction.transactionType === "credit" ? "payment received" : "payment transferred")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="body1" color="text.secondary">
                  Seems like no credit history
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </DashboardLayout>
  )
}

export default CreditManagementPage
